import { Box, Grid, Typography } from "@material-ui/core";
import { MultiContratoModel } from "api/touchone/models/multi-contrato";
import { TokenGestaoEmpresaModel } from "api/touchone/models/token-gestao-empresa";
import { FormLogin } from "components/forms/form-login/form-login";
import { DefaultFormRefs } from "components/forms/utils";
import { useNotificacoes } from "components/notificacoes";
import { CircularLoading } from "components/utils/circular-loading";
import { LoginFormModel } from "models/app/forms/auth/login-form";
import { EnumTipoPerfil } from "models/enums/enum-tipo-perfil";
import React from "react";
import { useHistory } from "react-router-dom";
import { useAuth } from "services/hooks/auth";
import { useEmpresaAtual } from "services/hooks/empresa";
import { usePessoaUsuario } from "services/hooks/pessoa-usuario";
import { EnumDialogs, useDialogStore } from "services/store/dialog-store";
import { useMesaStore } from "services/store/mesa-store";
import { useObterToken } from "services/store/obter-token";
import { convertToken } from "utils/convert-token";

export type LoginProps = {
    loginRefs: React.RefObject<DefaultFormRefs<LoginFormModel>>
    isCordova: boolean
    callback?: () => void
}

export const Login = ({ loginRefs, isCordova, callback }: LoginProps) => {
    const desvincularMesa = useMesaStore(state => state.desvincularMesa);
    const { getEmpresaAtual } = useEmpresaAtual()
    const empresaId = getEmpresaAtual()?.id ?? ''

    const history = useHistory()
    const { auth, carregando: carregandoAuth } = useAuth()
    const { showErrorMessage, showSuccessMessage } = useNotificacoes()
    const openCloseDialog = useDialogStore(state => state.openCloseDialog);
    const { isTokenExpired, setToken } = useObterToken();
    const { carregarPessoa, carregando: carregandoCarregarPessoa } = usePessoaUsuario()

    const carregando = carregandoCarregarPessoa || carregandoAuth

    const efetuarLogin = async (model: LoginFormModel, contratoId: string) => {
        try {
            let res = await auth({
                usuario: model.usuario,
                senha: model.senha,
                contratoId: contratoId
            })

            if (res.erro && res.statusCode !== 300) throw res.erro
            if (res.statusCode === 300) {
                const contratos = res.resultado?.data as MultiContratoModel[]
                if (empresaId) {
                    const temAMesmaEmpresa = contratos.filter(x => x.id === empresaId)
                    if (temAMesmaEmpresa.length === 0) throw new Error('Você não tem uma conta vinculada a esta empresa, faça um novo pedido')
                    res = await auth({
                        usuario: model.usuario,
                        senha: model.senha,
                        contratoId: temAMesmaEmpresa[0].contratoId ?? ''
                    })
                } else {
                    openCloseDialog<{ contratos: MultiContratoModel[], handleSelecionarContrato: (contratoId: string) => void }>
                        (EnumDialogs.SelecionarContrato, true, {
                            contratos: contratos,
                            handleSelecionarContrato: async (contratoId: string) => {
                                await efetuarLogin(model, contratoId)
                            }
                        })
                    return
                }
            }
            const resultado = res.resultado?.data

            const token = resultado?.accessToken
            if (!token) throw new Error('Nenhum token encontrado')
            if (isTokenExpired(token)) throw new Error('Token inválido')

            const getTokenInfo = convertToken(token)
            if (!getTokenInfo) throw new Error('Erro ao converter token')

            const temAMesmaEmpresa = getTokenInfo.empresa.filter(x => x.Id === empresaId)
            if (temAMesmaEmpresa.length === 0) {
                const isAdm = getTokenInfo.empresa[0].Perfil.Tipo === EnumTipoPerfil.Operador
                if (!isAdm) throw new Error('Você não tem uma conta vinculada a esta empresa, faça um novo pedido')

                if (getTokenInfo.empresa.length === 1) {
                    desvincularMesa();
                    await carregarPessoa(token)
                    setToken(token)
                    localStorage.setItem("empresaMobile", empresaId);
                    openCloseDialog(EnumDialogs.Autenticar, false)
                    history.push(`/${getTokenInfo.empresa[0]?.Id ?? ''}`)
                    return
                }
                openCloseDialog<{ empresas: TokenGestaoEmpresaModel[], handleSelecionarEmpresa: (empresaId: string) => Promise<void> }>(EnumDialogs.SelecionarEmpresa, true, {
                    empresas: getTokenInfo.empresa,
                    handleSelecionarEmpresa: async (empresaId: string) => {
                        desvincularMesa();
                        await carregarPessoa(token)
                        setToken(token)
                        localStorage.setItem("empresaMobile", empresaId);
                        openCloseDialog(EnumDialogs.Autenticar, false)
                        history.push(`/${empresaId}`)
                    }
                })
                return
            }
            if (!empresaId) {
                desvincularMesa();
            }
            await carregarPessoa(token)
            setToken(token)
            callback?.()
            localStorage.setItem("empresaMobile", temAMesmaEmpresa[0].Id);

            openCloseDialog(EnumDialogs.Autenticar, false)
            openCloseDialog(EnumDialogs.SelecionarEmpresa, false)
            openCloseDialog(EnumDialogs.SelecionarContrato, false)
            showSuccessMessage('Login efetuado com sucesso')
            return


        } catch (error: any) {
            showErrorMessage(error.message)
        }
    }

    return (
        <Box p={2}>
            <Grid container spacing={2}>
                {carregando && <CircularLoading tipo="FULLSIZED" titulo="Carregando..." />}
                <Grid item xs={12}>
                    <Typography variant="body1">
                        {isCordova ? (
                            <>
                                Entre na conta para acessar as configurações como administrador.
                            </>
                        ) : <>
                            Faça login e mantenha suas informações salvas para este e os próximos pedidos.
                        </>}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <FormLogin
                        showLoading={false}
                        ref={loginRefs}
                        loading={carregando}
                        onSubmit={(model) => {
                            efetuarLogin(model, '');
                        }}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}